import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { Box, CircularProgress } from '@mui/material';
import {
  useHospitalLogoUrl,
  useAuthAccessToken,
  useIsCaregiverPendingApproval,
  useIsLoggedIn,
  useIsUserDoneIneligible,
  useLoggedInUser,
} from 'features/auth/hooks/useAuthData';
import { useLazyGetUserQuery } from 'services/authService';
import { useAppDispatch } from 'store/store';
import {
  setHospitalLogoUrl,
  setIsCaregiverWaitingForApproval,
  setIsLoggedIn,
  setIsUserDoneIneligible,
  setUser,
} from 'features/auth/authSlice';
import { useCobrandedPath, useNavigate } from 'features/cobrand/hooks/useCobrandUtils';
import { useEnrollUserMutation } from 'services/signupService';
import { ProgramUser } from 'features/auth/types';
import { isCaregiverPendingApprovalError } from 'features/signUp/utils/errorUtils';
import { setServerCobrandSku } from 'features/cobrand/cobrandSlice';
import { parseServerSku } from 'features/cobrand/types';
import { DoneIneligibleState } from 'components/DoneIneligibleState/DoneIneligibleState';
import { useLazyGetMostRecentPoliciesQuery } from 'services/settingsService';
import { PrivacyPolicyReconsent } from 'components/PrivacyPolicyReconsent/PrivacyPolicyReconsent';
import { useFeatureFlags } from 'features/DeveloperTools/hooks/useFeatureFlags';
import { FeatureFlagKeys } from 'features/DeveloperTools/config/constants';
import { HospitalLogoSplash } from 'components/HospitalLogoSplash/HospitalLogoSplash';
import { useSplash } from 'features/splashScreen/hooks/useSplash';

const shouldEnrollUser = (getUserData: ProgramUser): boolean =>
  getUserData.approved_status && getUserData.status === 'registered';

const userIsDoneIneligible = (getUserData: ProgramUser): boolean => {
  const { eligible, wellframe_membership_state: membershipState } = getUserData;
  return (
    membershipState === 'done' ||
    membershipState === 'terminated' ||
    (membershipState === 'enrolled' && !eligible)
  );
};

export const AuthGuard = () => {
  const [getUser, { isLoading: isGettingUser, isUninitialized: isGetUserUninitialized }] =
    useLazyGetUserQuery();
  const isLoggedIn = useIsLoggedIn();
  const isCaregiverPendingApproval = useIsCaregiverPendingApproval();
  const isUserDoneIneligible = useIsUserDoneIneligible();
  const storedHospitalLogoUrl = useHospitalLogoUrl();
  const loggedInUser = useLoggedInUser();
  const accessToken = useAuthAccessToken();
  const location = useLocation();
  const [requestedLocation, setRequestedLocation] = useState<string | null>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [enrollUser] = useEnrollUserMutation();
  const caregiverApprovalPagePath = useCobrandedPath('/pending-approval');
  const homePagePath = useCobrandedPath('/home');
  const loginPagePath = useCobrandedPath('/login');
  const [getMostRecentPolicies, { isLoading: isGettingPolicies }] =
    useLazyGetMostRecentPoliciesQuery();
  const [showReconsent, setShowReconsent] = useState(false);
  const isAuthLoading =
    Boolean(accessToken) && (isGettingUser || isGetUserUninitialized || isGettingPolicies);
  const hospitalLogoUrl = storedHospitalLogoUrl ?? loggedInUser?.hospital?.client_logo_dark_url;

  // Extract returnUrl from location state (set by AppLinkGuard on login redirect)
  const returnUrl = location.state?.returnUrl as string | undefined;

  const {
    showSplash: showAuthSplash,
    showSpinner,
    isLogoVisible,
    splashLogoUrl,
    handleLogoLoad,
    handleLogoError,
  } = useSplash({
    isLoading: isAuthLoading,
    hospitalLogoUrl,
    isEnabled: Boolean(accessToken),
  });

  // Fetch the user data when the access token is set
  useEffect(() => {
    const triggerGetUser = async () => {
      const { data: userData, error: getUserError } = await getUser();

      // Handle caregiver pending approval state
      if (getUserError && isCaregiverPendingApprovalError(getUserError)) {
        dispatch(setIsCaregiverWaitingForApproval({ value: true, persist: true }));
        return;
      }

      if (userData) {
        if (shouldEnrollUser(userData)) {
          await enrollUser({
            wellframeUserId: userData.id.toString(),
            activeUserToken: userData.active_user_token,
          });
        }
        const { data: mostRecentPolicies } = await getMostRecentPolicies(userData.code);
        if (
          mostRecentPolicies?.eula_policy.signature_required ||
          mostRecentPolicies?.privacy_policy.signature_required
        )
          setShowReconsent(true);
        dispatch(setHospitalLogoUrl(userData.hospital?.client_logo_dark_url));
        dispatch(setIsUserDoneIneligible(userIsDoneIneligible(userData)));
        dispatch(setServerCobrandSku(parseServerSku(userData.app_sku)));
        dispatch(setUser(userData));
        dispatch(setIsLoggedIn(true));
        dispatch(setIsCaregiverWaitingForApproval({ value: false, persist: true }));
      }
    };

    if (accessToken) {
      triggerGetUser();
    }
  }, [accessToken]);

  // This is done so that in case the route changes by any chance through other
  // means between the moment of request and the render we navigate to the initially
  // requested route.
  useEffect(() => {
    if (requestedLocation && location.pathname !== requestedLocation) {
      setRequestedLocation(null);
      navigate(requestedLocation);
    }
  }, [requestedLocation, location.pathname]);

  // Handle returnUrl navigation after successful authentication
  // This enables app link auth preservation (e.g., enrollment deep links)
  useEffect(() => {
    if (isLoggedIn && returnUrl && !isAuthLoading) {
      // Navigate to the returnUrl that was preserved during login redirect
      navigate(returnUrl, { replace: true });
    }
  }, [isLoggedIn, returnUrl, isAuthLoading, navigate]);

  // getUser call is ongoing -> show loading state
  if (showAuthSplash) {
    return (
      <HospitalLogoSplash
        logoUrl={splashLogoUrl}
        showSpinner={showSpinner}
        isLogoVisible={isLogoVisible}
        onLogoLoad={handleLogoLoad}
        onLogoError={handleLogoError}
      />
    );
  }

  if (isAuthLoading) {
    return (
      <Box justifyContent="center" alignItems="center" height="100vh" width="100vw" display="flex">
        <CircularProgress />
      </Box>
    );
  }

  // User is done/ineligible -> show done/ineligible screen
  if (isUserDoneIneligible) {
    const contactLink = {
      type: loggedInUser?.hospital.contact_link_type,
      value: loggedInUser?.hospital.contact_link,
    };
    return <DoneIneligibleState contactLink={contactLink} />;
  }

  // Caregiver pending approval logic -> redirect to /pending-approval
  if (isCaregiverPendingApproval) {
    // To avoid infinite loop, we first redirect to /pending-approval, and then render the outlet once there
    if (location.pathname === caregiverApprovalPagePath) {
      return <Outlet />;
    }
    return <Navigate to={caregiverApprovalPagePath} replace />;
  }

  // Caregiver approved logic -> redirect away from /pending-approval
  if (location.pathname === caregiverApprovalPagePath && isLoggedIn) {
    return <Navigate to={homePagePath} replace />;
  }

  if (showReconsent) {
    // Redirect to policies acceptance page if latest policies require signature
    return <PrivacyPolicyReconsent onReconsent={() => setShowReconsent(false)} />;
  }

  // User is logged in -> render the requested route
  return isLoggedIn ? <Outlet /> : <Navigate to={loginPagePath} replace />;
};
